import {useState, useEffect, useRef} from 'react'
import {Modal, Button, Form, Alert} from 'react-bootstrap'
import PropTypes from 'prop-types'
import ButtonWithIcon from './ButtonWithIcon.jsx'
import {Icons} from './Icons.jsx'
import {env} from '../../../env.js'
import {BarChartAnaliza} from './charts/index.js'
import {SimpleTable} from './simpleTable/SimpleTable.jsx'
import {ExportToExcel} from './ExportToExcel.jsx'

const ChatMessage = ({message}) => {
    const isUser = message.role === 'user'

    const renderChart = () => {
        if (!message.chartData) return null

        try {
            const chartConfig = typeof message.chartData === 'string'
                ? JSON.parse(message.chartData)
                : message.chartData

            if (chartConfig.type === 'bar_chart' && !chartConfig.error) {
                return (
                    <div className="mt-3">
                        <BarChartAnaliza
                            data={chartConfig.data}
                            xDatakey={chartConfig.xDatakey}
                            dataKeys={chartConfig.dataKeys}
                            title={chartConfig.title}
                            hideExelExport={true}
                            hideCopyToclipboard={true}
                            tooltipKey={null}
                        />
                    </div>
                )
            } else if (chartConfig.error) {
                return (
                    <Alert variant="warning" className="mt-2">
                        Błąd generowania wykresu: {chartConfig.error}
                    </Alert>
                )
            }
        } catch (e) {
            console.error('Error rendering chart:', e, message.chartData)
            return null
        }

        return null
    }

    const renderTable = () => {
        if (!message.tableData) return null

        try {
            const tableConfig = typeof message.tableData === 'string'
                ? JSON.parse(message.tableData)
                : message.tableData

            if (tableConfig.type === 'table' && !tableConfig.error) {
                if (!tableConfig.columns || tableConfig.columns.length === 0) {
                    return (
                        <Alert variant="warning" className="mt-2">
                            Table configuration is invalid
                        </Alert>
                    )
                }


                return (
                    <div className="mt-3" style={{maxWidth: '100%', overflowX: 'auto'}}>
                        {tableConfig.title && (
                            <h6 className="mb-2">{tableConfig.title}</h6>
                        )}
                        <SimpleTable
                            columns={tableConfig.columns}
                            data={tableConfig.data || []}
                            hidePagination={false}
                            hideGlobalFilter={true}
                            hideColumnVisibility={true}
                            hideExport={true}
                            hideSorting={false}
                        />
                        {message.worksheetToolResultId && tableConfig.data && (
                            <div className={'m-2'}>

                                <ExportToExcel
                                    data={tableConfig.data}
                                    fileName={`${tableConfig.title || 'raport'}.xlsx`}
                                    text="Pobierz raport do Excela"
                                    variant="primary"
                                    size={28}

                                />
                            </div>
                        )}

                    </div>

                )
            } else if (tableConfig.error) {
                return (
                    <Alert variant="warning" className="mt-2">
                        Unable to generate table: {tableConfig.error}
                    </Alert>
                )
            }
        } catch (e) {
            return (
                <Alert variant="danger" className="mt-2">
                    Unable to display table
                </Alert>
            )
        }

        return null
    }

    return (
        <div className={`d-flex ${isUser ? 'justify-content-end' : 'justify-content-start'}`}>
            <div
                className={`p-2 rounded ${isUser ? 'bg-primary text-white' : 'bg-light'}`}
                style={{maxWidth: isUser ? '75%' : '95%'}}
            >
                {message.content}
                {renderChart()}
                {renderTable()}
                {message.toolInvocations && message.toolInvocations.length > 0 && (
                    <div className="mt-2 pt-2 border-top d-flex gap-1">
                        <small className="text-muted d-block mb-1" style={{fontSize: '0.75rem'}}>Użyte
                            narzędzia:</small>
                        {message.toolInvocations.map((tool, idx) => (
                            <div key={idx} className="mb-1 ">
                                <code className="bg-dark text-light p-1 rounded" style={{fontSize: '0.65rem'}}>
                                    {tool.toolName}
                                </code>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

ChatMessage.propTypes = {
    message: PropTypes.shape({
        id: PropTypes.string,
        role: PropTypes.string,
        content: PropTypes.string,
        toolInvocations: PropTypes.array,
        chartData: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        tableData: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        worksheetToolResultId: PropTypes.string,
    }).isRequired,
}

export const AIChatModalContent = ({show, handleClose, apiEndpoint, initialMessages, size}) => {
    const messagesEndRef = useRef(null)
    const [inputValue, setInputValue] = useState('')
    const [messages, setMessages] = useState(initialMessages || [])
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState(null)

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({behavior: 'smooth'})
    }, [messages])

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!inputValue.trim() || isLoading) return

        const userMessage = {
            id: `user-${Date.now()}`,
            role: 'user',
            content: inputValue.trim()
        }

        setMessages(prev => [...prev, userMessage])
        setInputValue('')
        setIsLoading(true)
        setError(null)

        const aiMessageId = `ai-${Date.now()}`
        let accumulatedContent = ''
        const toolInvocations = []
        let chartData = null
        let tableData = null

        setMessages(prev => [...prev, {
            id: aiMessageId,
            role: 'assistant',
            content: '',
            toolInvocations: [],
            chartData: null,
            tableData: null,
            worksheetToolResultId: null
        }])

        try {
            const conversationMessages = messages.map(msg => ({
                role: msg.role,
                content: msg.content
            }))
            conversationMessages.push({
                role: userMessage.role,
                content: userMessage.content
            })

            const response = await fetch(apiEndpoint || `${env.AI_CHAT_API_URL}/api/v1/query`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    messages: conversationMessages
                })
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const reader = response.body.getReader()
            const decoder = new TextDecoder()

            while (true) {
                const {done, value} = await reader.read()

                if (done) break

                const chunk = decoder.decode(value, {stream: true})
                const lines = chunk.split('\n').filter(line => line.trim())

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        const dataStr = line.substring(6)

                        if (dataStr === '[DONE]') {
                            continue
                        }

                        try {
                            const parsed = JSON.parse(dataStr)

                            if (parsed.text) {
                                accumulatedContent += parsed.text
                                setMessages(prev => prev.map(msg =>
                                    msg.id === aiMessageId
                                        ? {...msg, content: accumulatedContent}
                                        : msg
                                ))
                            } else if (parsed.tool_call) {
                                toolInvocations.push({
                                    toolName: parsed.tool_call.name,
                                    id: parsed.tool_call.id
                                })
                                setMessages(prev => prev.map(msg =>
                                    msg.id === aiMessageId
                                        ? {...msg, toolInvocations: [...toolInvocations]}
                                        : msg
                                ))
                            } else if (parsed.tool_result) {
                                const currentTool = toolInvocations.find(t => t.id === parsed.tool_result.id)
                                if (currentTool && currentTool.toolName === 'generate_bar_chart') {
                                    chartData = parsed.tool_result.result
                                    setMessages(prev => prev.map(msg =>
                                        msg.id === aiMessageId
                                            ? {...msg, chartData}
                                            : msg
                                    ))
                                }
                                if (currentTool && currentTool.toolName === 'generate_worksheet_json_data') {
                                    tableData = parsed.tool_result.result
                                    setMessages(prev => prev.map(msg =>
                                        msg.id === aiMessageId
                                            ? {...msg, tableData, worksheetToolResultId: parsed.tool_result.id}
                                            : msg
                                    ))
                                }
                            } else if (parsed.error) {
                                throw new Error(parsed.error.message)
                            }
                        } catch (parseErr) {
                            console.warn('Failed to parse chunk:', dataStr, parseErr)
                        }
                    }
                }
            }

            if (!accumulatedContent && toolInvocations.length === 0) {
                throw new Error('No content or tool calls received from AI')
            }
        } catch (err) {
            console.error('Error sending message:', err)
            setError(err)
            setMessages(prev => prev.filter(msg => msg.id !== aiMessageId))
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Modal size={size || 'xl'} show={show} onHide={handleClose} style={{maxWidth: '95vw', margin: '1rem auto'}}>
            <Modal.Header closeButton>
                <Modal.Title>Czat AI - Rozmawiaj z bazą danych</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {error && (
                    <Alert variant="danger" className="mb-3">
                        Błąd połączenia z AI: {error.message}
                    </Alert>
                )}

                <div
                    className="d-flex flex-column gap-2"
                    style={{
                        maxHeight: '70vh',
                        overflowY: 'auto',
                        padding: '1rem',
                        border: '1px solid #dee2e6',
                        borderRadius: '0.25rem',
                        backgroundColor: '#f8f9fa'
                    }}
                >
                    {messages.length === 0 && (
                        <div className="text-center text-muted p-4">
                            <Icons.help size={48} className="mb-2"/>
                            <p>Zadaj pytanie, aby rozpocząć konwersację</p>
                        </div>
                    )}

                    {messages.map((message) => (
                        <ChatMessage key={message.id} message={message}/>
                    ))}

                    {isLoading && (
                        <div className="d-flex justify-content-start">
                            <div className="p-2 bg-light rounded">
                                <span className="spinner-border spinner-border-sm me-2"/>
                                AI pisze...
                            </div>
                        </div>
                    )}

                    <div ref={messagesEndRef}/>
                </div>

                <Form onSubmit={handleSubmit} className="mt-3">
                    <Form.Group className="d-flex gap-2">
                        <Form.Control
                            type="text"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            placeholder="Zadaj pytanie..."
                            disabled={isLoading}
                        />
                        <Button
                            type="submit"
                            variant="success"
                            disabled={isLoading || !inputValue.trim()}
                        >
                            Wyślij
                        </Button>
                    </Form.Group>
                </Form>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Zamknij
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

AIChatModalContent.propTypes = {
    show: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    apiEndpoint: PropTypes.string,
    initialMessages: PropTypes.array,
    size: PropTypes.oneOf(['sm', 'lg', 'xl']),
}

const AIChatModal = ({
                         triggerButtonVariant = 'primary',
                         triggerButtonText = 'Czat AI',
                         triggerButtonIcon,
                         size,
                         apiEndpoint,
                         initialMessages,
                     }) => {
    const [show, setShow] = useState(false)

    return (
        <>
            <ButtonWithIcon
                icon={triggerButtonIcon || <Icons.help/>}
                text={triggerButtonText}
                variant={triggerButtonVariant}
                onClick={() => setShow(true)}
            />

            {show && (
                <AIChatModalContent
                    show={show}
                    handleClose={() => setShow(false)}
                    size={size}
                    apiEndpoint={apiEndpoint}
                    initialMessages={initialMessages}
                />
            )}
        </>
    )
}

AIChatModal.propTypes = {
    size: PropTypes.oneOf(['sm', 'lg', 'xl']),
    triggerButtonVariant: PropTypes.string,
    triggerButtonText: PropTypes.string,
    triggerButtonIcon: PropTypes.element,
    apiEndpoint: PropTypes.string,
    initialMessages: PropTypes.array,
}

export default AIChatModal
